import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import logoImg from "../../assets/Logo.png";


interface NavItem {
  path: string;
  label: string;
  icon: string;
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { path: '/feed', label: 'Лента', icon: '📰' },
  { path: '/profile', label: 'Профиль', icon: '' },
  { path: '/messages', label: 'Сообщения', icon: '💬' },
  { path: '/stickers', label: 'Стикерпаки', icon: '🎨' },
  { path: '/clan', label: 'Мой Клан', icon: '🐺' },
  { path: '/events', label: 'События', icon: '🏄' },
  { path: '/battles', label: 'Битвы', icon: '⚔️' },
  { path: '/shop', label: 'Магазин', icon: '🛒' },
  { path: '/contact', label: 'Связь', icon: '📞' },
  { path: '/sponsorship', label: 'Спонсорство', icon: '💎' },
  { path: '/rules', label: 'Правила', icon: '📜' },
  { path: '/admin', label: 'Админка', icon: '️', adminOnly: true },
];

interface AppNavbarProps {
  userId: string | null;
  isAdmin: boolean;
  onLogout: () => void;
  securityOperationLocked?: boolean;
}

const AppNavbar: React.FC<AppNavbarProps> = ({
  isAdmin,
  onLogout,
  securityOperationLocked = false,
}) => {
  const visibleNavItems = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin);
  const mobilePaths = ['/feed', '/messages', '/events', '/profile'];
  const primaryMobileItems = mobilePaths.flatMap(path =>
    visibleNavItems.filter(item => item.path === path));
  const overflowMobileItems = visibleNavItems.filter(item => !mobilePaths.includes(item.path));
  const [mobileOverflowOpen, setMobileOverflowOpen] = useState(false);

  const handleLogout = () => {
    if (securityOperationLocked) return;
    onLogout();
  };

  const guardNavigation = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!securityOperationLocked) return;
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <>
      <nav aria-label="Основная навигация" className="shark-panel shark-border shark-density-navbar hidden md:flex flex-col w-64 h-screen fixed left-0 top-0 border-r z-50">
        <div className="mb-8 px-2 flex items-center gap-3">
          <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400 uppercase tracking-tight">
            SHARKLY
          </h1>
          <img
            src={logoImg}
            alt="Sharkly Logo"
            className="h-8 w-auto object-contain opacity-90 hover:opacity-100 transition transform hover:scale-110"
          />
        </div>

        <div className="shark-density-list flex flex-1 flex-col overflow-y-auto custom-scrollbar">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              aria-disabled={securityOperationLocked}
              tabIndex={securityOperationLocked ? -1 : undefined}
              onClick={guardNavigation}
              className={({ isActive }) =>
                `shark-density-control flex items-center gap-3 rounded-xl transition-all duration-200 relative ${
                  isActive 
                    ? 'bg-blue-600/20 text-blue-400 font-bold border border-blue-500/30 shark-accent-text' 
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                } ${securityOperationLocked ? 'opacity-40 cursor-not-allowed' : ''}`
              }
            >
              <span className="text-xl">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>

        <div className="mt-auto pt-4 border-t border-gray-800">
          <button
            onClick={handleLogout}
            disabled={securityOperationLocked}
            aria-disabled={securityOperationLocked}
            className="shark-density-control w-full flex items-center gap-3 rounded-xl text-red-400 hover:bg-red-500/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <span>🚪</span> Выйти
          </button>
        </div>
      </nav>

      <nav aria-label="Мобильная навигация" className="shark-panel shark-border md:hidden fixed bottom-0 left-0 right-0 backdrop-blur-lg border-t z-50 pb-safe">
        {mobileOverflowOpen && (
          <div id="mobile-navigation-overflow" aria-label="Дополнительная навигация" className="shark-card shark-border absolute bottom-full left-2 right-2 mb-2 grid grid-cols-2 gap-2 rounded-2xl border shadow-xl">
            {overflowMobileItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                aria-disabled={securityOperationLocked}
                tabIndex={securityOperationLocked ? -1 : undefined}
                onClick={(event) => {
                  guardNavigation(event);
                  if (!securityOperationLocked) setMobileOverflowOpen(false);
                }}
                className={({ isActive }) => `shark-density-control flex items-center gap-2 rounded-xl text-sm font-bold ${isActive ? 'bg-blue-600/20 shark-accent-text' : 'text-gray-400 hover:bg-gray-800'} ${securityOperationLocked ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                <span aria-hidden="true">{item.icon}</span><span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        )}
        <div className="flex justify-around items-center p-2">
          {primaryMobileItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              aria-disabled={securityOperationLocked}
              tabIndex={securityOperationLocked ? -1 : undefined}
              onClick={guardNavigation}
              className={({ isActive }) =>
                `shark-density-control flex flex-col items-center rounded-lg transition relative ${
                  isActive ? 'text-blue-400 shark-accent-text' : 'text-gray-500'
                } ${securityOperationLocked ? 'opacity-40 cursor-not-allowed' : ''}`
              }
            >
              <span className="text-2xl mb-1">{item.icon}</span>
              <span className="text-[10px] font-medium">{item.label}</span>
            </NavLink>
          ))}
          {overflowMobileItems.length > 0 && <button
            type="button"
            aria-label="Ещё"
            aria-expanded={mobileOverflowOpen}
            aria-controls="mobile-navigation-overflow"
            disabled={securityOperationLocked}
            onClick={() => setMobileOverflowOpen((open) => !open)}
            className={`shark-density-control flex flex-col items-center rounded-lg text-[10px] font-medium text-gray-500 ${securityOperationLocked ? 'opacity-40 cursor-not-allowed' : ''}`}
          ><span className="mb-1 text-2xl" aria-hidden="true">•••</span><span>Ещё</span></button>}
        </div>
      </nav>
    </>
  );
};

export default AppNavbar;
